import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { LoaderCircle, Send } from "lucide-react";
import { useEffect, useRef, useState } from "react";

import { Button, IconButton, Input } from "@/components/ui";
import { apiFetch } from "@/lib/api/client";
import { keys } from "@/lib/api/keys";

import {
  type Bubble,
  BubbleView,
  ProgressStrip,
  type SetupProgress,
} from "./components/SetupBubbles";

/**
 * La bienvenida: configurar el espacio hablando con Clara.
 *
 * Configurar un agente son cinco pestañas y unos veinte campos. Quien entra por
 * primera vez no sabe qué es un «propósito», ni qué diferencia hay entre una
 * regla y una FAQ: el formulario le pide vocabulario antes de darle valor.
 *
 * Hablando cuenta lo que hace su negocio —lo único que realmente sabe— y de ahí
 * sale el resto. Y es la primera vez que ve a Clara trabajar: si la
 * conversación es buena, ya entendió el producto sin que nadie se lo explique.
 *
 * **Se puede saltear.** Un alta obligatoria es una pared: quien vino a mirar el
 * tablero tiene que poder entrar, y lo que falte se completa desde el editor.
 */

type Progress = SetupProgress;

interface Reply {
  reply: string;
  progress: Progress;
  saved: string[];
}

interface State {
  started: boolean;
  completed: boolean;
  agent_id: number | null;
  messages: { role: string; content: string }[];
  progress: Progress;
}

export function ClaraSetup({ onSkip, onManual }: { onSkip: () => void; onManual: () => void }) {
  const queryClient = useQueryClient();
  /* Lo que pasó **en esta pantalla**. El hilo que ya estaba guardado sale de la
     consulta y no se copia acá: copiarlo daría dos fuentes de verdad para lo
     mismo, y la que se quedara vieja mandaría igual. */
  const [nuevas, setNuevas] = useState<Bubble[]>([]);
  const [text, setText] = useState("");
  const [progresoLocal, setProgresoLocal] = useState<Progress | null>(null);
  const log = useRef<HTMLDivElement>(null);
  const arrancado = useRef(false);

  const estado = useQuery({
    queryKey: keys.onboarding,
    queryFn: () => apiFetch<State>("/api/v1/onboarding"),
  });

  const empezar = useMutation({
    mutationFn: () => apiFetch<Reply>("/api/v1/onboarding/start", { method: "POST" }),
    onSuccess: (respuesta) => {
      setNuevas([{ role: "assistant", content: respuesta.reply }]);
      setProgresoLocal(respuesta.progress);
    },
  });

  const responder = useMutation({
    mutationFn: (mensaje: string) =>
      apiFetch<Reply>("/api/v1/onboarding/message", {
        method: "POST",
        body: { text: mensaje },
      }),
    onSuccess: (respuesta) => {
      setNuevas((previas) => [...previas, { role: "assistant", content: respuesta.reply }]);
      setProgresoLocal(respuesta.progress);
    },
  });

  const terminar = useMutation({
    mutationFn: () => apiFetch<State>("/api/v1/onboarding/finish", { method: "POST" }),
    onSuccess: async () => {
      // El perfil del negocio cambió: el menú y las pantallas dependen de él.
      await queryClient.invalidateQueries();
      onSkip();
    },
  });

  /* Lo que ya estaba guardado, más lo de esta pantalla. Derivado y no copiado:
     así retomar una charla de ayer y seguir escribiendo hoy son lo mismo. */
  const guardadas = (estado.data?.messages ?? []).filter(
    (item): item is Bubble => item.role === "user" || item.role === "assistant",
  );
  const bubbles = estado.data ? [...guardadas, ...nuevas] : null;
  const progress = progresoLocal ?? estado.data?.progress ?? null;

  // Sin nada guardado, Clara saluda primero. Es lo único que hace el efecto.
  useEffect(() => {
    if (arrancado.current || !estado.data || guardadas.length > 0) return;
    arrancado.current = true;
    empezar.mutate();
  }, [estado.data, guardadas.length, empezar]);

  useEffect(() => {
    log.current?.scrollTo({ top: log.current.scrollHeight, behavior: "smooth" });
  }, [bubbles?.length, responder.isPending]);

  function enviar(event: React.FormEvent) {
    event.preventDefault();
    const mensaje = text.trim();
    if (!mensaje || responder.isPending) return;
    setNuevas((previas) => [...previas, { role: "user", content: mensaje }]);
    setText("");
    responder.mutate(mensaje);
  }

  const error = estado.error ?? empezar.error ?? responder.error ?? terminar.error;

  return (
    <main className="bg-background flex min-h-dvh flex-col items-center px-4 py-12 sm:px-6">
      <img
        src="/clara-redonda.png"
        alt=""
        aria-hidden
        width={144}
        height={144}
        className="size-[72px] rounded-full object-cover"
      />
      {/* El subtítulo va afuera del `h1`: anidado, el nombre accesible del
          encabezado pasa a ser las dos frases juntas. */}
      <h1 className="mt-3.5 text-[28px] font-semibold">Hola, soy Clara</h1>
      <p className="text-muted-foreground mt-1.5 max-w-[460px] text-center text-[15px]">
        Contame de tu negocio y configuro todo por vos. Después lo ajustás cuando quieras.
      </p>

      <div className="mt-7 w-full max-w-[560px]">
        <div ref={log} className="max-h-[26rem] min-h-40 space-y-3 overflow-y-auto">
          {bubbles === null ? (
            <p className="text-muted-foreground text-center text-sm">Un segundo…</p>
          ) : (
            bubbles.map((bubble, index) => <BubbleView key={index} bubble={bubble} />)
          )}
          {responder.isPending && (
            <p className="text-muted-foreground flex items-center gap-2 text-sm">
              <LoaderCircle aria-hidden className="size-3.5 animate-spin" />
              Clara está escribiendo…
            </p>
          )}
        </div>

        {error && (
          <p role="alert" className="text-danger mt-3 text-sm">
            {error.message}
          </p>
        )}

        <form onSubmit={enviar} className="mt-4 flex gap-2">
          <Input
            value={text}
            onChange={(event) => setText(event.target.value)}
            placeholder="Contale a Clara…"
            aria-label="Tu respuesta"
            disabled={bubbles === null || responder.isPending}
            className="rounded-full px-4"
          />
          <IconButton
            type="submit"
            icon={Send}
            label="Enviar"
            disabled={!text.trim() || responder.isPending}
            className="bg-primary text-primary-foreground hover:bg-primary-hover hover:text-primary-foreground size-11 rounded-full"
          />
        </form>

        {progress && <ProgressStrip progress={progress} />}

        {/* Dos salidas, y las dos hacen falta. Quien ya contó lo suyo quiere
            entrar; quien no quiere charlar necesita igual decir a qué se dedica,
            porque sin el rubro la aplicación no sabe si mostrar Propiedades. */}
        <div className="mt-7 flex flex-col items-center gap-2">
          <Button size="lg" loading={terminar.isPending} onClick={() => terminar.mutate()}>
            {progress && progress.done > 0
              ? "Ir a mi tablero"
              : "Saltear, lo configuro después"}
          </Button>
          <Button variant="ghost" size="sm" onClick={onManual}>
            Prefiero elegirlo de una lista
          </Button>
        </div>
      </div>
    </main>
  );
}
